import { useEffect, useMemo, useState } from 'react'
import supabase from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const money = (v) => new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }).format(Number(v || 0))
const dt = (v) => {
  if (!v) return '—'
  try { return new Intl.DateTimeFormat('el-GR', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(v)) } catch { return v }
}
const inputClass = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100'
const blankLine = () => ({ item_type: 'service', product_id: '', service_id: '', description: '', quantity: 1, unit_price: '', cost_price: '', notes: '' })
const blankJob = () => ({
  id: null,
  client_id: '',
  location_id: '',
  assignment_id: '',
  asset_id: '',
  title: '',
  job_type: 'service',
  status: 'new',
  priority: 'normal',
  requested_work: '',
  performed_work: '',
  appointment_start: '',
  appointment_end: '',
  billing_type: 'flat',
  agreed_price: '',
  actual_charge: '',
  paid_status: 'unpaid',
  notes: '',
  google_sync_enabled: false
})

function localDateTime(value) {
  if (!value) return ''
  const d = new Date(value)
  const offset = d.getTimezoneOffset()
  return new Date(d.getTime() - offset * 60000).toISOString().slice(0, 16)
}

function toIso(value) {
  return value ? new Date(value).toISOString() : null
}

export default function JobsCalendarManager() {
  const [rows, setRows] = useState([])
  const [clients, setClients] = useState([])
  const [locations, setLocations] = useState([])
  const [assignments, setAssignments] = useState([])
  const [assets, setAssets] = useState([])
  const [services, setServices] = useState([])
  const [products, setProducts] = useState([])
  const [calendarConnection, setCalendarConnection] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('open')
  const [editorOpen, setEditorOpen] = useState(false)
  const [form, setForm] = useState(blankJob())
  const [lineItems, setLineItems] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => { load() }, [])

  async function load() {
    try {
      setLoading(true)
      setError('')
      const [jobsRes, clientsRes, locationsRes, assignmentsRes, assetsRes, servicesRes, productsRes, calendarsRes] = await Promise.all([
        supabase.rpc('codevaro_jobs_admin_list_v2'),
        supabase.from('codevaro_clients').select('id,name,legal_name').order('name'),
        supabase.from('codevaro_client_locations').select('id,client_id,name,address,city,status').order('name'),
        supabase.from('codevaro_client_service_assignments').select('id,client_id,service_id,location_id,display_name,status'),
        supabase.from('codevaro_assets').select('id,client_id,location_id,assignment_id,asset_type,custom_brand,custom_model,serial_number,fiscal_serial,terminal_id,status'),
        supabase.from('codevaro_services').select('id,title,slug').order('title'),
        supabase.from('codevaro_products').select('id,sku,name,category,brand,model,cost_price,sell_price,is_active').eq('is_active', true).order('name'),
        supabase.from('codevaro_calendar_connections').select('*').eq('is_active', true).eq('provider', 'google').limit(1)
      ])
      for (const result of [jobsRes, clientsRes, locationsRes, assignmentsRes, assetsRes, servicesRes, productsRes, calendarsRes]) {
        if (result.error) throw result.error
      }
      setRows(jobsRes.data || [])
      setClients(clientsRes.data || [])
      setLocations(locationsRes.data || [])
      setAssignments(assignmentsRes.data || [])
      setAssets(assetsRes.data || [])
      setServices(servicesRes.data || [])
      setProducts(productsRes.data || [])
      setCalendarConnection((calendarsRes.data || [])[0] || null)
    } catch (err) {
      console.error(err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((row) => {
      const status = String(row.status || '').toLowerCase()
      const isClosed = ['completed', 'done', 'cancelled'].includes(status)
      const matchesFilter = filter === 'all' || (filter === 'open' && !isClosed) || (filter === 'closed' && isClosed) || (filter === 'calendar' && row.google_sync_enabled)
      const haystack = [row.client_name, row.location_name, row.service_title, row.asset_label, row.title, row.job_type, row.requested_work, row.performed_work].filter(Boolean).join(' ').toLowerCase()
      return matchesFilter && (!q || haystack.includes(q))
    })
  }, [rows, search, filter])

  const selectedLocations = useMemo(() => locations.filter((x) => x.client_id === form.client_id), [locations, form.client_id])
  const selectedAssignments = useMemo(() => assignments.filter((x) => x.client_id === form.client_id), [assignments, form.client_id])
  const selectedAssets = useMemo(() => assets.filter((x) => x.client_id === form.client_id), [assets, form.client_id])
  const serviceById = useMemo(() => Object.fromEntries(services.map((x) => [x.id, x])), [services])

  function newJob() {
    setForm(blankJob())
    setLineItems([])
    setEditorOpen(true)
  }

  async function editJob(job) {
    try {
      setError('')
      const { data: fullJob, error: jobError } = await supabase.from('codevaro_jobs').select('*').eq('id', job.id).single()
      if (jobError) throw jobError
      const { data: items, error: itemsError } = await supabase.from('codevaro_job_line_items').select('*').eq('job_id', job.id).order('created_at')
      if (itemsError) throw itemsError
      setForm({
        ...blankJob(),
        ...fullJob,
        client_id: fullJob.client_id || '',
        location_id: fullJob.location_id || '',
        assignment_id: fullJob.assignment_id || '',
        asset_id: fullJob.asset_id || '',
        appointment_start: localDateTime(fullJob.appointment_start),
        appointment_end: localDateTime(fullJob.appointment_end),
        agreed_price: fullJob.agreed_price ?? '',
        actual_charge: fullJob.actual_charge ?? ''
      })
      setLineItems((items || []).map((item) => ({ ...item, product_id: item.product_id || '', service_id: item.service_id || '', unit_price: item.unit_price ?? '', cost_price: item.cost_price ?? '' })))
      setEditorOpen(true)
    } catch (err) {
      console.error(err)
      setError(`Αποτυχία φόρτωσης εργασίας: ${err.message}`)
    }
  }

  function changeClient(clientId) {
    setForm((f) => ({ ...f, client_id: clientId, location_id: '', assignment_id: '', asset_id: '' }))
  }

  function addLine(type = 'service') {
    setLineItems((items) => [...items, { ...blankLine(), item_type: type }])
  }

  function updateLine(index, patch) {
    setLineItems((items) => items.map((item, i) => i === index ? { ...item, ...patch } : item))
  }

  function removeLine(index) {
    setLineItems((items) => items.filter((_, i) => i !== index))
  }

  function selectProduct(index, productId) {
    const product = products.find((x) => x.id === productId)
    updateLine(index, {
      product_id: productId,
      service_id: '',
      description: product ? [product.name, product.brand, product.model].filter(Boolean).join(' · ') : '',
      unit_price: product?.sell_price ?? '',
      cost_price: product?.cost_price ?? ''
    })
  }

  function selectService(index, serviceId) {
    const service = serviceById[serviceId]
    updateLine(index, { service_id: serviceId, product_id: '', description: service?.title || '' })
  }

  const linesTotal = useMemo(() => lineItems.reduce((sum, item) => sum + ((Number(item.quantity) || 0) * (Number(item.unit_price) || 0)), 0), [lineItems])

  async function saveJob(e) {
    e.preventDefault()
    if (!form.client_id || !form.title.trim()) {
      setError('Ο πελάτης και ο τίτλος εργασίας είναι υποχρεωτικά.')
      return
    }
    try {
      setSaving(true)
      setError('')
      const payload = {
        client_id: form.client_id,
        location_id: form.location_id || null,
        assignment_id: form.assignment_id || null,
        asset_id: form.asset_id || null,
        title: form.title.trim(),
        job_type: form.job_type || 'service',
        status: form.status || 'new',
        priority: form.priority || 'normal',
        requested_work: form.requested_work || null,
        performed_work: form.performed_work || null,
        appointment_start: toIso(form.appointment_start),
        appointment_end: toIso(form.appointment_end),
        scheduled_date: form.appointment_start ? form.appointment_start.slice(0, 10) : null,
        billing_type: form.billing_type || 'flat',
        agreed_price: form.agreed_price === '' ? null : Number(form.agreed_price),
        actual_charge: form.actual_charge === '' ? (lineItems.length ? linesTotal : null) : Number(form.actual_charge),
        paid_status: form.paid_status || 'unpaid',
        notes: form.notes || null,
        google_sync_enabled: Boolean(form.google_sync_enabled),
        completed_at: ['completed', 'done'].includes(form.status) ? (form.completed_at || new Date().toISOString()) : null
      }

      let jobId = form.id
      if (form.id) {
        const { error: updateError } = await supabase.from('codevaro_jobs').update(payload).eq('id', form.id)
        if (updateError) throw updateError
      } else {
        const { data: created, error: insertError } = await supabase.from('codevaro_jobs').insert({ ...payload, source: 'admin' }).select('id').single()
        if (insertError) throw insertError
        jobId = created.id
      }

      if (form.id) {
        const { error: deleteLinesError } = await supabase.from('codevaro_job_line_items').delete().eq('job_id', jobId)
        if (deleteLinesError) throw deleteLinesError
      }
      const cleanLines = lineItems
        .filter((item) => item.description?.trim())
        .map((item) => ({
          job_id: jobId,
          item_type: item.item_type || 'service',
          product_id: item.product_id || null,
          service_id: item.service_id || null,
          description: item.description.trim(),
          quantity: Number(item.quantity) || 1,
          unit_price: item.unit_price === '' ? null : Number(item.unit_price),
          cost_price: item.cost_price === '' ? null : Number(item.cost_price),
          line_total: (Number(item.quantity) || 1) * (Number(item.unit_price) || 0),
          notes: item.notes || null
        }))
      if (cleanLines.length) {
        const { error: linesError } = await supabase.from('codevaro_job_line_items').insert(cleanLines)
        if (linesError) throw linesError
      }

      if (payload.google_sync_enabled && payload.appointment_start) {
        const { data: existingLinks, error: linkReadError } = await supabase.from('codevaro_job_calendar_links').select('id').eq('job_id', jobId).eq('provider', 'google').limit(1)
        if (linkReadError) throw linkReadError
        if (!(existingLinks || []).length) {
          const { error: linkError } = await supabase.from('codevaro_job_calendar_links').insert({
            job_id: jobId,
            provider: 'google',
            calendar_id: calendarConnection?.calendar_id || null,
            sync_status: 'pending'
          })
          if (linkError) throw linkError
        }
      }

      setEditorOpen(false)
      setForm(blankJob())
      setLineItems([])
      await load()
    } catch (err) {
      console.error(err)
      setError(`Αποτυχία αποθήκευσης: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Jobs / Calendar</h1>
          <p className="mt-1 text-sm text-slate-600">Ραντεβού, τεχνικές εργασίες, προϊόντα/υπηρεσίες, χρεώσεις και Google Calendar linkage.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Πελάτης, service, εργασία..." className="min-w-[280px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm"><option value="open">Open</option><option value="all">All</option><option value="closed">Completed</option><option value="calendar">Calendar linked</option></select>
          <button onClick={newJob} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"><SafeIcon icon={FiIcons.FiPlus} className="h-4 w-4" />New Job</button>
        </div>
      </div>
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800"><strong>Google Calendar:</strong> {calendarConnection ? `jobs can be queued for “${calendarConnection.calendar_name || 'Google Calendar'}”.` : 'no active calendar connection is configured.'} Automatic API push/pull still requires website OAuth credentials; queued jobs remain safely pending until then.</div>
      {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
      {loading ? <div className="flex h-48 items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" /></div> : (
        <div className="space-y-3">
          {filtered.map((job) => (
            <button key={job.id} onClick={() => editJob(job)} className="block w-full rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2"><h2 className="text-lg font-semibold text-slate-900">{job.title || job.job_type || 'Job'}</h2><Status value={job.status} /><Priority value={job.priority} /></div>
                  <p className="mt-1 text-sm text-slate-500">{job.client_name}{job.location_name ? ` · ${job.location_name}` : ''}{job.service_title ? ` · ${job.service_title}` : ''}</p>
                </div>
                <div className="text-right text-sm"><div className="font-medium text-slate-900">{dt(job.appointment_start || job.scheduled_date)}</div><div className="text-xs text-slate-500">{job.asset_label || 'No linked asset'}</div></div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5"><Small label="Type" value={job.job_type} /><Small label="Agreed" value={money(job.agreed_price)} /><Small label="Charged" value={money(job.actual_charge)} /><Small label="Payment" value={job.paid_status} /><Small label="Calendar" value={job.calendar_sync_status || (job.google_sync_enabled ? 'Pending OAuth' : 'Not linked')} /></div>
              {job.requested_work ? <div className="mt-4 whitespace-pre-wrap text-sm text-slate-700"><span className="font-medium">Request: </span>{job.requested_work}</div> : null}
              {job.source ? <div className="mt-3 text-xs text-slate-400">Source: {job.source}{job.source_ref ? ` · ${job.source_ref}` : ''}</div> : null}
            </button>
          ))}
          {!filtered.length ? <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500">Δεν υπάρχουν εργασίες για αυτό το φίλτρο.</div> : null}
        </div>
      )}

      {editorOpen ? (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/50 p-3 backdrop-blur-sm sm:p-6">
          <form onSubmit={saveJob} className="mx-auto max-w-6xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
              <div><h2 className="text-xl font-semibold text-slate-900">{form.id ? 'Edit Job' : 'New Job'}</h2><p className="text-sm text-slate-500">Service visit, appointment, extra work and products sold.</p></div>
              <button type="button" onClick={() => setEditorOpen(false)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><SafeIcon icon={FiIcons.FiX} className="h-5 w-5" /></button>
            </div>

            <div className="space-y-6 p-5 sm:p-6">
              <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <SelectField label="Client *" value={form.client_id} onChange={(e) => changeClient(e.target.value)}><option value="">Select client</option>{clients.map((x) => <option key={x.id} value={x.id}>{x.name || x.legal_name}</option>)}</SelectField>
                <SelectField label="Location" value={form.location_id} onChange={(e) => setForm((f) => ({ ...f, location_id: e.target.value }))}><option value="">No location</option>{selectedLocations.map((x) => <option key={x.id} value={x.id}>{x.name}{x.city ? ` · ${x.city}` : ''}</option>)}</SelectField>
                <SelectField label="Service assignment" value={form.assignment_id} onChange={(e) => setForm((f) => ({ ...f, assignment_id: e.target.value }))}><option value="">No service assignment</option>{selectedAssignments.map((x) => <option key={x.id} value={x.id}>{x.display_name || serviceById[x.service_id]?.title || 'Service'}</option>)}</SelectField>
                <SelectField label="Equipment" value={form.asset_id} onChange={(e) => setForm((f) => ({ ...f, asset_id: e.target.value }))}><option value="">No equipment</option>{selectedAssets.map((x) => <option key={x.id} value={x.id}>{[x.asset_type, x.custom_brand, x.custom_model, x.serial_number || x.fiscal_serial || x.terminal_id].filter(Boolean).join(' · ')}</option>)}</SelectField>
              </section>

              <section className="rounded-xl border border-slate-200 p-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <TextField label="Title *" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="xl:col-span-2" />
                  <SelectField label="Type" value={form.job_type} onChange={(e) => setForm((f) => ({ ...f, job_type: e.target.value }))}><option value="service">Service</option><option value="installation">Installation</option><option value="repair">Repair</option><option value="support">Support</option><option value="sale">Sale</option><option value="training">Training</option><option value="other">Other</option></SelectField>
                  <SelectField label="Priority" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></SelectField>
                  <SelectField label="Status" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}><option value="new">New</option><option value="scheduled">Scheduled</option><option value="in_progress">In progress</option><option value="waiting">Waiting</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option></SelectField>
                  <TextField label="Appointment start" type="datetime-local" value={form.appointment_start} onChange={(e) => setForm((f) => ({ ...f, appointment_start: e.target.value }))} />
                  <TextField label="Appointment end" type="datetime-local" value={form.appointment_end} onChange={(e) => setForm((f) => ({ ...f, appointment_end: e.target.value }))} />
                  <label className="flex items-end gap-2 pb-2 text-sm text-slate-700"><input type="checkbox" checked={form.google_sync_enabled} onChange={(e) => setForm((f) => ({ ...f, google_sync_enabled: e.target.checked }))} className="h-4 w-4" /> Queue for Google Calendar</label>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <AreaField label="Requested work" value={form.requested_work} onChange={(e) => setForm((f) => ({ ...f, requested_work: e.target.value }))} />
                  <AreaField label="Performed work" value={form.performed_work} onChange={(e) => setForm((f) => ({ ...f, performed_work: e.target.value }))} />
                </div>
              </section>

              <section className="rounded-xl border border-slate-200 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div><h3 className="font-semibold text-slate-900">Products & services sold</h3><p className="text-sm text-slate-500">Add extra troubleshooting, installation work, routers, switches, cables or any other product/service.</p></div>
                  <div className="flex gap-2"><button type="button" onClick={() => addLine('service')} className="rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50">+ Service</button><button type="button" onClick={() => addLine('product')} className="rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50">+ Product</button></div>
                </div>
                <div className="mt-4 space-y-3">
                  {lineItems.map((item, index) => (
                    <div key={item.id || index} className="grid grid-cols-1 gap-3 rounded-xl bg-slate-50 p-3 lg:grid-cols-12">
                      <SelectField label="Type" value={item.item_type} onChange={(e) => updateLine(index, { item_type: e.target.value, product_id: '', service_id: '' })} className="lg:col-span-2"><option value="service">Service</option><option value="product">Product</option><option value="fee">Fee</option><option value="discount">Discount</option></SelectField>
                      <div className="lg:col-span-3">{item.item_type === 'product' ? <SelectField label="Catalogue" value={item.product_id} onChange={(e) => selectProduct(index, e.target.value)}><option value="">Custom product</option>{products.map((x) => <option key={x.id} value={x.id}>{x.name}{x.sell_price != null ? ` · ${money(x.sell_price)}` : ''}</option>)}</SelectField> : <SelectField label="Catalogue" value={item.service_id} onChange={(e) => selectService(index, e.target.value)}><option value="">Custom service</option>{services.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}</SelectField>}</div>
                      <TextField label="Description" value={item.description} onChange={(e) => updateLine(index, { description: e.target.value })} className="lg:col-span-3" />
                      <TextField label="Qty" type="number" step="0.01" value={item.quantity} onChange={(e) => updateLine(index, { quantity: e.target.value })} className="lg:col-span-1" />
                      <TextField label="Unit €" type="number" step="0.01" value={item.unit_price} onChange={(e) => updateLine(index, { unit_price: e.target.value })} className="lg:col-span-1" />
                      <div className="lg:col-span-1"><div className="mb-1 text-xs font-medium text-slate-500">Total</div><div className="py-2 text-sm font-semibold text-slate-900">{money((Number(item.quantity) || 0) * (Number(item.unit_price) || 0))}</div></div>
                      <div className="flex items-end lg:col-span-1"><button type="button" onClick={() => removeLine(index)} className="rounded-lg p-2 text-red-600 hover:bg-red-50"><SafeIcon icon={FiIcons.FiTrash2} className="h-4 w-4" /></button></div>
                    </div>
                  ))}
                  {!lineItems.length ? <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">No billable line items yet.</div> : null}
                </div>
                <div className="mt-4 text-right text-lg font-semibold text-slate-900">Line items total: {money(linesTotal)}</div>
              </section>

              <section className="grid grid-cols-1 gap-4 rounded-xl border border-slate-200 p-4 md:grid-cols-2 xl:grid-cols-5">
                <SelectField label="Billing" value={form.billing_type} onChange={(e) => setForm((f) => ({ ...f, billing_type: e.target.value }))}><option value="flat">Flat</option><option value="hourly">Hourly</option><option value="included">Included</option><option value="warranty">Warranty</option><option value="no_charge">No charge</option></SelectField>
                <TextField label="Agreed €" type="number" step="0.01" value={form.agreed_price} onChange={(e) => setForm((f) => ({ ...f, agreed_price: e.target.value }))} />
                <TextField label="Actual charge €" type="number" step="0.01" value={form.actual_charge} onChange={(e) => setForm((f) => ({ ...f, actual_charge: e.target.value }))} placeholder={lineItems.length ? String(linesTotal.toFixed(2)) : ''} />
                <SelectField label="Payment" value={form.paid_status} onChange={(e) => setForm((f) => ({ ...f, paid_status: e.target.value }))}><option value="unpaid">Unpaid</option><option value="partial">Partial</option><option value="paid">Paid</option><option value="included">Included</option><option value="waived">Waived</option></SelectField>
                <TextField label="Notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
              </section>
            </div>

            <div className="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setEditorOpen(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving…' : form.id ? 'Save Changes' : 'Create Job'}</button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  )
}

function TextField({ label, className = '', ...props }) { return <label className={className}><div className="mb-1 text-xs font-medium text-slate-500">{label}</div><input {...props} className={inputClass} /></label> }
function SelectField({ label, children, className = '', ...props }) { return <label className={className}><div className="mb-1 text-xs font-medium text-slate-500">{label}</div><select {...props} className={inputClass}>{children}</select></label> }
function AreaField({ label, ...props }) { return <label><div className="mb-1 text-xs font-medium text-slate-500">{label}</div><textarea {...props} rows={4} className={inputClass} /></label> }
function Small({ label, value }) { return <div className="rounded-lg bg-slate-50 p-3"><div className="text-[10px] uppercase text-slate-400">{label}</div><div className="mt-1 text-sm font-medium text-slate-800">{value || '—'}</div></div> }
function Status({ value }) { const v = String(value || '').toLowerCase(); const c = ['completed', 'done'].includes(v) ? 'bg-green-100 text-green-800' : ['cancelled', 'failed'].includes(v) ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'; return <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${c}`}>{value || 'open'}</span> }
function Priority({ value }) { return value ? <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">{value}</span> : null }